import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { env } from "../config";

const SearchPage = () => {
  const [searchWord, setSearchWord] = useState("");
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [pageData, setPageData] = useState({
    current_page: 1,
    last_page: 1,
  });

  const location = useLocation();

  const getPaginationRange = (current, last) => {
    const delta = 2;
    const range = [];
    for (
      let i = Math.max(2, current - delta);
      i <= Math.min(last - 1, current + delta);
      i++
    ) {
      range.push(i);
    }
    if (current - delta > 2) {
      range.unshift("...");
    }
    if (current + delta < last - 1) {
      range.push("...");
    }
    range.unshift(1);
    if (last > 1) {
      range.push(last);
    }
    return range;
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const query = queryParams.get("q") || "";
    setSearchWord(query);

    if (query) {
      executeSearch(query, 1);
    }
  }, [location.search]);

  const executeSearch = (searchTerm, page = 1) => {
    if (!searchTerm.trim()) return;

    setLoading(true);
    setHasSearched(true);

    axios
      .get(`${env.baseUrl}/api/search?q=${encodeURIComponent(searchTerm)}&page=${page}`)
      .then((res) => {
        setPosts(res.data.posts?.data || []);
        setEvents(res.data.events || []);
        setGuides(res.data.guides || []);

        setPageData({
          current_page: res.data.posts?.current_page || 1,
          last_page: res.data.posts?.last_page || 1,
        });

        setLoading(false);
      })
      .catch((err) => {
        console.error("Search error:", err);
        setPosts([]);
        setEvents([]);
        setGuides([]);
        setLoading(false);
      });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchWord.trim()) {
      executeSearch(searchWord, 1);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pageData.last_page) {
      executeSearch(searchWord, newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="search-page main-category-block inner-pages">
      <div className="container">
        <div className="title-block text-center">
          <h2>Search Results</h2>
        </div>

        {/* Search Bar */}
        <div className="search-bar-wrapper text-center my-4">
          <form onSubmit={handleSearchSubmit} className="search-form d-inline-flex">
            <input
              type="text"
              className="form-control me-2"
              placeholder="Search posts, events, guides..."
              value={searchWord}
              onChange={(e) => setSearchWord(e.target.value)}
              style={{ minWidth: "300px" }}
            />
            <button type="submit" className="btn btn-primary" style={{ backgroundColor: "#B12129", borderColor: "#B12129" }}>
              Search
            </button>
          </form>
        </div>

        {loading ? (
          <div className="text-center my-5">Loading search results...</div>
        ) : (
          hasSearched && (
            <div className="md-block">
              {/* Posts Section */}
              {posts.length > 0 && (
                <div className="results-section mb-5">
                  <h3>Posts</h3>
                  <div className="row">
                    {posts.map((post) => (
                      <div className="col-lg-4 col-md-6 mb-4" key={post.id}>
                        <div className="post-box">
                          <div className="post-image position-relative">
                            <div className="img-block">
                              <figure className="thumbnail zoom-effect">
                                <a href={`/post/${post.slug}`}>
                                  <img
                                    src={post.image || "/images/noimgpost.jpeg"}
                                    alt={post.title}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = "/images/noimgpost.jpeg";
                                    }}
                                  />
                                </a>
                              </figure>
                            </div>
                            <div className="post-content">
                              <div className="post-title">
                                <h2>
                                  <a href={`/post/${post.slug}`}>{post.title}</a>
                                </h2>
                              </div>
                              <div className="post-category">{post.category_id}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Events Section */}
              {events.length > 0 && (
                <div className="results-section mb-5">
                  <h3>Events</h3>
                  <div className="row">
                    {events.map((event) => (
                      <div className="col-lg-4 col-md-6 mb-4" key={event.id}>
                        <div className="post-box">
                          {event.image && (
                            <div className="img-block mb-2">
                              <img
                                src={event.image}
                                alt={event.title}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = "/images/noimgpost.jpeg";
                                }}
                                style={{ width: "100%", borderRadius: "6px" }}
                              />
                            </div>
                          )}
                          <div className="post-content">
                            <h2>
                              <a href={`/event/${event.slug}`}>{event.title}</a>
                            </h2>
                            <p>{event.date}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Guides Section */}
              {guides.length > 0 && (
                <div className="results-section mb-5">
                  <h3>Guides & Maps</h3>
                  <div className="row">
                    {guides.map((guide) => (
                      <div className="col-lg-4 col-md-6 mb-4" key={guide.id}>
                        <div className="post-box">
                          <div className="post-content">
                            <h2>
                              <a href={`/maps-category/${guide.slug}`}>{guide.title}</a>
                            </h2>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {posts.length === 0 && events.length === 0 && guides.length === 0 && (
                <div className="text-center my-5">No results found for "{searchWord}".</div>
              )}

              {/* Pagination */}
              {pageData.last_page > 1 && (
                <nav className="post-pagination" aria-label="Search navigation">
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        pageData.current_page === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(pageData.current_page - 1);
                        }}
                      >
                        &laquo; Previous
                      </button>
                    </li>

                    {getPaginationRange(pageData.current_page, pageData.last_page).map((item, index) => {
                      if (item === "...") {
                        return (
                          <li key={`dots-${index}`} className="page-item disabled">
                            <span className="page-link">&hellip;</span>
                          </li>
                        );
                      }
                      return (
                        <li
                          key={item}
                          className={`page-item ${
                            item === pageData.current_page ? "active" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(item);
                            }}
                          >
                            {item}
                          </button>
                        </li>
                      );
                    })}

                    <li
                      className={`page-item ${
                        pageData.current_page === pageData.last_page
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(pageData.current_page + 1);
                        }}
                      >
                        Next &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SearchPage;
